/**
 * Editor Theme Picker
 *
 * A panel component for switching themes in the flow editor.
 * Works independently of AIContext/AIProvider, imports themes directly.
 */

import React, { useState, useMemo } from 'react';
import { ALL_THEMES, FEATURED_THEMES, getThemeById } from '../../ai/styles/themeLibrary';
import { StyleTheme } from '../../ai/schemas/StyleTheme';

type CategoryFilter = 'all' | 'light' | 'dark' | 'colorful' | 'professional' | 'minimal';

interface EditorThemePickerProps {
  isOpen: boolean;
  onClose: () => void;
  currentThemeId: string;
  onSelectTheme: (themeId: string) => void;
}

const CATEGORY_TABS: { label: string; value: CategoryFilter }[] = [
  { label: 'All', value: 'all' },
  { label: 'Light', value: 'light' },
  { label: 'Dark', value: 'dark' },
  { label: 'Colorful', value: 'colorful' },
  { label: 'Professional', value: 'professional' },
  { label: 'Minimal', value: 'minimal' },
];

function ThemeCard({
  theme,
  isSelected,
  onClick,
}: {
  theme: StyleTheme;
  isSelected: boolean;
  onClick: () => void;
}) {
  const colors = theme.colors;
  const accent2 = colors.accent?.[1] || colors.secondary || colors.primary;
  return (
    <button
      className={`editor-theme-card${isSelected ? ' selected' : ''}`}
      onClick={onClick}
      title={theme.name}
    >
      <div
        className="editor-theme-card-preview"
        style={{ backgroundColor: colors.background, position: 'relative', overflow: 'hidden' }}
      >
        {/* Diagonal accent strip for visual variety */}
        <div style={{
          position: 'absolute',
          top: 0,
          right: -10,
          width: 40,
          height: '100%',
          backgroundColor: accent2,
          opacity: 0.18,
          transform: 'skewX(-12deg)',
        }} />
        <div
          className="editor-theme-card-accent"
          style={{ backgroundColor: colors.primary }}
        />
        <div className="editor-theme-card-lines">
          <div
            className="editor-theme-card-title-line"
            style={{ backgroundColor: colors.textPrimary }}
          />
          <div
            className="editor-theme-card-body-line"
            style={{ backgroundColor: colors.textSecondary }}
          />
          <div
            className="editor-theme-card-body-line short"
            style={{ backgroundColor: colors.textSecondary }}
          />
          {/* Small color chips showing the palette */}
          <div style={{ display: 'flex', gap: 3, marginTop: 6 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: colors.primary }} />
            <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: accent2 }} />
            <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: colors.accent?.[0] || colors.primary, opacity: 0.7 }} />
          </div>
        </div>
      </div>
      <span className="editor-theme-card-name">{theme.name}</span>
    </button>
  );
}

export function EditorThemePicker({
  isOpen,
  onClose,
  currentThemeId,
  onSelectTheme,
}: EditorThemePickerProps) {
  const [category, setCategory] = useState<CategoryFilter>('all');
  const [showAll, setShowAll] = useState(false);

  const filteredThemes = useMemo(() => {
    if (category === 'all') return showAll ? ALL_THEMES : FEATURED_THEMES;
    return ALL_THEMES.filter((t) => t.category === category);
  }, [category, showAll]);

  const currentTheme = getThemeById(currentThemeId);

  if (!isOpen) return null;

  return (
    <div className="editor-theme-picker">
      <div className="editor-theme-picker-header">
        <div>
          <h3>Themes</h3>
          <div className="editor-theme-picker-current">
            <span
              className="editor-theme-picker-current-swatch"
              style={{ backgroundColor: currentTheme?.colors.primary || '#6366F1' }}
            />
            Current Theme: {currentTheme?.name || 'Unknown'}
          </div>
        </div>
        <button
          className="editor-theme-picker-close"
          onClick={onClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="18" height="18">
            <path
              fill="currentColor"
              d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
            />
          </svg>
        </button>
      </div>
      <div className="editor-theme-picker-tabs">
        {CATEGORY_TABS.map((tab) => (
          <button
            key={tab.value}
            className={`editor-theme-picker-tab${category === tab.value ? ' active' : ''}`}
            onClick={() => setCategory(tab.value)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className="editor-theme-picker-grid">
        {filteredThemes.map((theme) => (
          <ThemeCard
            key={theme.id}
            theme={theme}
            isSelected={theme.id === currentThemeId}
            onClick={() => {
              onSelectTheme(theme.id);
              onClose();
            }}
          />
        ))}
      </div>
      {category === 'all' && !showAll && (
        <button className="editor-theme-show-all" onClick={() => setShowAll(true)}>
          Show all themes ({ALL_THEMES.length})
        </button>
      )}
    </div>
  );
}

export default EditorThemePicker;
